import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PageLayout } from "@/components/layout/PageLayout";
import { Helmet } from "react-helmet-async";
import { CoinGrid } from "@/components/coins/CoinGrid";
import { Sparkles, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function NewCoins() {
  const { data: coins, isLoading } = useQuery({
    queryKey: ["new-coins"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("coins")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data ?? [];
    },
    refetchInterval: 30000,
  });

  const now = Date.now();
  const last24h = coins?.filter((c) => c.created_at && now - c.created_at < 86400000) ?? [];
  const lastHour = coins?.filter((c) => c.created_at && now - c.created_at < 3600000) ?? [];

  return (
    <>
      <Helmet>
        <title>New Coins - TheMoonRun</title>
        <meta name="description" content="Discover newly launched meme coins on Solana. Be early to the next moonshot." />
      </Helmet>

      <PageLayout>
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-yellow-500/20 to-orange-500/20 flex items-center justify-center">
            <Sparkles className="w-6 h-6 sm:w-7 sm:h-7 text-yellow-500" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-display font-bold text-foreground">New Coins</h1>
            <p className="text-sm text-muted-foreground">Fresh launches on Solana</p>
          </div>
        </div>

        {/* Stats */}
        <div className="flex gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary text-sm">
            <Clock className="w-4 h-4 text-primary" />
            <span className="font-medium">{lastHour.length} in last hour</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary text-sm">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="font-medium">{last24h.length} in 24h</span>
          </div>
          <Badge variant="outline" className="animate-pulse">
            Auto-refreshing
          </Badge>
        </div>

        {/* Results */}
        <CoinGrid coins={coins ?? []} isLoading={isLoading} />

        {coins && coins.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <Sparkles className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">No new coins yet</p>
          </div>
        )}
      </PageLayout>
    </>
  );
}
